import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toastActions } from "./toast";
import { getRandId, readURIFromFile } from "../libs";
import { currentUserActions } from "./currentUser";
import { NavigateFunction } from "react-router-dom";
import { getCsrfToken } from "../utils/csrf";

// 型定義をインポート（再エクスポート）
export type {
  FloorPlanStyle,
  AspectRatio,
  SelectionMode,
  SelectionArea,
  FloorPlanImage,
  FloorPlanAfterImage,
  BandTemplate,
  LocalBandTemplate,
  FloorPlanExtractionState,
  PdfConvertedPage,
} from "../types/floorPlanExtraction";

import type { PdfConvertedPage } from "../types/floorPlanExtraction";

import type {
  FloorPlanStyle,
  AspectRatio,
  SelectionMode,
  SelectionArea,
  FloorPlanImage,
  FloorPlanAfterImage,
  BandTemplate,
  LocalBandTemplate,
  FloorPlanExtractionState,
} from "../types/floorPlanExtraction";

// 定数
const MaxUploadFileSize = 5 * 1024 * 1024; // 5MB

// 初期状態
const initialState: FloorPlanExtractionState = {
  isLoading: false,
  stubId: getRandId(),
  sourceImage: null,
  afterImages: [],
  selectedAfterImage: undefined,
  selectionMode: 'rectangle',
  selectionArea: null,
  selectionPreviewUrl: null,
  selectionPreviewBlob: null,
  selectedStyle: 'normal',
  brushSize: 30,
  drawingLines: [],
  selectionHistoryStack: [],
  selectionHistoryIndex: -1,
  showSelectionCanvas: false,
  showConfirmPopup: false,
  isPhotoTooBigPopupShow: false,
  runTime: 0,
  maxExecTime: 60,
  // 帯替え用
  bandSourceImage: null,
  bandInputSource: 'upload',
  bandTemplates: [],
  bandTemplatesCount: 0,
  bandTemplatesMaxCount: 10,
  bandTemplatesLoading: false,
  localBandTemplate: null,
  selectedBandTemplate: null,
  bandPosition: 'top',
  bandYOffset: 0,
  bandScale: 1.0,
  bandResultImage: null,
  // PDF複数ページ対応
  pdfConvertedPages: [],
  currentPdfPageIndex: 0,
};

// RootState型
interface RootState {
  floorPlanExtraction: FloorPlanExtractionState;
  [key: string]: unknown;
}

// 画像を追加する非同期アクション
const addSourceImage = createAsyncThunk<void, File, { state: RootState }>(
  "floorPlanExtraction/addSourceImage",
  async (file, thunkAPI) => {
    // ファイルタイプチェック
    if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/webp") {
      thunkAPI.dispatch(toastActions.toastError('対応していないファイル形式です。PNG、JPEG、WebPのみ対応しています。'));
      return;
    }

    // ファイルサイズチェック
    if (file.size > MaxUploadFileSize) {
      thunkAPI.dispatch(floorPlanExtractionActions.setIsPhotoTooBigPopupShow(true));
      return;
    }

    const uri = await readURIFromFile(file) as string;
    const stubId = getRandId();

    const newImage: FloorPlanImage = {
      stubId,
      file,
      uri,
      isProcessed: false,
      isProcessedFail: false,
    };

    thunkAPI.dispatch(floorPlanExtractionActions.setSourceImage(newImage));
    thunkAPI.dispatch(floorPlanExtractionActions.setShowSelectionCanvas(true));
  }
);

// 間取り抜き出し処理の引数型
interface ProcessFloorPlanArgs {
  navigate: NavigateFunction;
  croppedImageBlob?: Blob;
}

// 間取り抜き出し処理を実行する非同期アクション
const processFloorPlanExtraction = createAsyncThunk<void, ProcessFloorPlanArgs, { state: RootState }>(
  "floorPlanExtraction/process",
  async ({ navigate, croppedImageBlob }, thunkAPI) => {
    const state = thunkAPI.getState().floorPlanExtraction;
    const { sourceImage, selectionPreviewBlob, selectedStyle, selectionMode, stubId } = state;

    if (!sourceImage || !selectionPreviewBlob) {
      thunkAPI.dispatch(toastActions.toastError('画像と選択範囲を指定してください。'));
      return;
    }

    thunkAPI.dispatch(floorPlanExtractionActions.setIsLoading(true));

    // タイマー処理
    let runTime = 0;
    thunkAPI.dispatch(floorPlanExtractionActions.setRunTime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(floorPlanExtractionActions.setRunTime(runTime += 1));
    }, 1000);

    try {
      // FormDataを作成
      const formData = new FormData();
      formData.append('stub_id', stubId.toString());
      formData.append('source_image', sourceImage.file);
      formData.append('selection_image', selectionPreviewBlob, 'selection.png');
      formData.append('style', selectedStyle);
      formData.append('aspect_ratio', 'original');
      formData.append('selection_type', selectionMode);

      // トリミングした画像を追加（外部APIに送信、PNG形式で透明背景を保持）
      if (croppedImageBlob) {
        // 元のファイル名をベースにしたファイル名を生成
        const originalName = sourceImage.file.name;
        let baseName = originalName.replace(/\.[^/.]+$/, ''); // 拡張子を除去
        // ファイル名が長すぎる場合は65文字にカット（合計100文字以内に収める）
        if (baseName.length > 65) {
          baseName = baseName.substring(0, 65);
        }
        const timestamp = Date.now();
        formData.append('cropped_image', croppedImageBlob, `${baseName}_cropped_${timestamp}.png`);
      }

      // APIを呼び出し
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/room-designs/floor-plan/process/', {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        body: formData,
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        // 成功時の処理
        const afterImage: FloorPlanAfterImage = {
          id: result.after_image_id,
          before_image_id: result.before_image_id,
          serverURI: result.after_image_url,
          fileName: result.after_image_file_name || `floor_plan_${selectedStyle}.jpg`,
          style: selectedStyle,
          aspectRatio: 'original',
          createdAt: new Date().toISOString(),
        };

        thunkAPI.dispatch(floorPlanExtractionActions.addAfterImage(afterImage));
        thunkAPI.dispatch(floorPlanExtractionActions.setSelectedAfterImage(afterImage.id));

        // 元画像を処理済みに更新
        thunkAPI.dispatch(floorPlanExtractionActions.setSourceImageProcessed(true));

        thunkAPI.dispatch(toastActions.toastSuccess('間取り抜き出しが完了しました。'));
      } else {
        // エラー時の処理
        thunkAPI.dispatch(floorPlanExtractionActions.setSourceImageFailed(true));
        thunkAPI.dispatch(toastActions.toastError(result.message || '処理中にエラーが発生しました。'));
      }
    } catch (error) {
      console.error('FloorPlan processing error:', error);
      thunkAPI.dispatch(floorPlanExtractionActions.setSourceImageFailed(true));
      thunkAPI.dispatch(toastActions.toastError('ネットワークエラーが発生しました。'));
    } finally {
      clearInterval(timer);
      thunkAPI.dispatch(floorPlanExtractionActions.setIsLoading(false));
      thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate));
    }
  }
);

// 帯替え用画像を追加する非同期アクション
const addBandSourceImage = createAsyncThunk<void, File, { state: RootState }>(
  "floorPlanExtraction/addBandSourceImage",
  async (file, thunkAPI) => {
    // ファイルタイプチェック
    if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/webp") {
      thunkAPI.dispatch(toastActions.toastError('対応していないファイル形式です。PNG、JPEG、WebPのみ対応しています。'));
      return;
    }

    // ファイルサイズチェック
    if (file.size > MaxUploadFileSize) {
      thunkAPI.dispatch(floorPlanExtractionActions.setIsPhotoTooBigPopupShow(true));
      return;
    }

    const uri = await readURIFromFile(file) as string;
    const stubId = getRandId();

    const newImage: FloorPlanImage = {
      stubId,
      file,
      uri,
      isProcessed: false,
      isProcessedFail: false,
    };

    thunkAPI.dispatch(floorPlanExtractionActions.setBandSourceImage(newImage));
    thunkAPI.dispatch(floorPlanExtractionActions.setBandInputSource('upload'));
  }
);

// 帯テンプレート一覧を取得する非同期アクション
const fetchBandTemplates = createAsyncThunk<void, void, { state: RootState }>(
  "floorPlanExtraction/fetchBandTemplates",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(floorPlanExtractionActions.setBandTemplatesLoading(true));
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/band-templates/', {
        method: 'GET',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });
      const data = await response.json();
      thunkAPI.dispatch(floorPlanExtractionActions.setBandTemplates(data.results || []));
      thunkAPI.dispatch(floorPlanExtractionActions.setBandTemplatesCount(data.count || 0));
      thunkAPI.dispatch(floorPlanExtractionActions.setBandTemplatesMaxCount(data.max_count || 10));
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('帯テンプレートの取得に失敗しました。'));
    } finally {
      thunkAPI.dispatch(floorPlanExtractionActions.setBandTemplatesLoading(false));
    }
  }
);

// 帯テンプレートをサーバーに保存する非同期アクション
const saveBandTemplate = createAsyncThunk<void, { name: string; image: File; position: 'top' | 'bottom' }, { state: RootState }>(
  "floorPlanExtraction/saveBandTemplate",
  async ({ name, image, position }, thunkAPI) => {
    try {
      const state = thunkAPI.getState().floorPlanExtraction;
      if (state.bandTemplatesCount >= state.bandTemplatesMaxCount) {
        thunkAPI.dispatch(toastActions.toastError(`登録上限（${state.bandTemplatesMaxCount}個）に達しています。`));
        return;
      }

      const csrfToken = getCsrfToken();
      const formData = new FormData();
      formData.append('name', name);
      formData.append('image', image);
      formData.append('position', position);

      const response = await fetch('/api/band-templates/', {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        thunkAPI.dispatch(toastActions.toastSuccess('帯テンプレートを登録しました。'));
        // テンプレート一覧を再取得
        thunkAPI.dispatch(fetchBandTemplates());
        // ローカルテンプレートをクリア
        thunkAPI.dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
      } else {
        const data = await response.json();
        thunkAPI.dispatch(toastActions.toastError(data.error || '帯テンプレートの登録に失敗しました。'));
      }
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('帯テンプレートの登録に失敗しました。'));
    }
  }
);

// 帯テンプレートを削除する非同期アクション
const deleteBandTemplate = createAsyncThunk<void, number, { state: RootState }>(
  "floorPlanExtraction/deleteBandTemplate",
  async (templateId, thunkAPI) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(`/api/band-templates/${templateId}/`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      if (response.ok) {
        thunkAPI.dispatch(toastActions.toastSuccess('帯テンプレートを削除しました。'));
        // テンプレート一覧を再取得
        thunkAPI.dispatch(fetchBandTemplates());
        // 選択中のテンプレートが削除された場合はクリア
        const state = thunkAPI.getState().floorPlanExtraction;
        if (state.selectedBandTemplate?.id === templateId) {
          thunkAPI.dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
        }
      } else {
        const data = await response.json();
        thunkAPI.dispatch(toastActions.toastError(data.error || '帯テンプレートの削除に失敗しました。'));
      }
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('帯テンプレートの削除に失敗しました。'));
    }
  }
);

// 帯テンプレートを更新する非同期アクション
const updateBandTemplate = createAsyncThunk<void, { id: number; name: string; position: 'top' | 'bottom' }, { state: RootState }>(
  "floorPlanExtraction/updateBandTemplate",
  async ({ id, name, position }, thunkAPI) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(`/api/band-templates/${id}/`, {
        method: 'PATCH',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, position }),
      });

      if (response.ok) {
        thunkAPI.dispatch(toastActions.toastSuccess('帯テンプレートを更新しました。'));
        // テンプレート一覧を再取得
        thunkAPI.dispatch(fetchBandTemplates());
      } else {
        const data = await response.json();
        thunkAPI.dispatch(toastActions.toastError(data.error || '帯テンプレートの更新に失敗しました。'));
      }
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('帯テンプレートの更新に失敗しました。'));
    }
  }
);

// 帯替えダウンロード時のクレジット消費（Mini/Liteプラン用）
const consumeBandReplacementCredit = createAsyncThunk<
  { success: boolean; credit_consumed?: number; credit_limit?: number },
  { downloadType: 'image' | 'pdf'; sourceFileName?: string },
  { state: RootState }
>(
  "floorPlanExtraction/consumeBandReplacementCredit",
  async ({ downloadType, sourceFileName }, thunkAPI) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/band-replacement/consume-credit/', {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          download_type: downloadType,
          source_file_name: sourceFileName,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // クレジット消費成功時、currentUserのcreditConsumedを更新
        if (data.credit_consumed !== undefined) {
          thunkAPI.dispatch(currentUserActions.setCreditConsumed(data.credit_consumed));
        }
        return { success: true, credit_consumed: data.credit_consumed, credit_limit: data.credit_limit };
      } else {
        thunkAPI.dispatch(toastActions.toastError(data.error || 'クレジットの消費に失敗しました。'));
        return { success: false };
      }
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('クレジットの消費に失敗しました。'));
      return { success: false };
    }
  }
);

// 帯テンプレートをローカルに追加する非同期アクション（プレビュー用）
const addBandTemplate = createAsyncThunk<void, File, { state: RootState }>(
  "floorPlanExtraction/addBandTemplate",
  async (file, thunkAPI) => {
    // ファイルタイプチェック
    if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/webp") {
      thunkAPI.dispatch(toastActions.toastError('対応していないファイル形式です。PNG、JPEG、WebPのみ対応しています。'));
      return;
    }

    // ファイルサイズチェック
    if (file.size > MaxUploadFileSize) {
      thunkAPI.dispatch(toastActions.toastError('ファイルサイズは5MB以内にしてください。'));
      return;
    }

    const uri = await readURIFromFile(file) as string;
    const id = `local-${getRandId()}`;
    const name = file.name.replace(/\.[^/.]+$/, ""); // 拡張子を除いたファイル名

    const state = thunkAPI.getState().floorPlanExtraction;
    const localTemplate: LocalBandTemplate = {
      id,
      name,
      imageUrl: uri,
      file,
      position: state.bandPosition,
    };

    thunkAPI.dispatch(floorPlanExtractionActions.setLocalBandTemplate(localTemplate));
  }
);

// 帯替え処理を実行する非同期アクション（モック）
const processBandReplacement = createAsyncThunk<void, NavigateFunction, { state: RootState }>(
  "floorPlanExtraction/processBandReplacement",
  async (navigate, thunkAPI) => {
    const state = thunkAPI.getState().floorPlanExtraction;
    const { bandSourceImage, bandInputSource, selectedBandTemplate, bandPosition, afterImages } = state;

    // 入力画像を決定
    const sourceImage = bandInputSource === 'extraction_result' && afterImages.length > 0
      ? afterImages[afterImages.length - 1]
      : bandSourceImage;

    if (!sourceImage || !selectedBandTemplate) {
      thunkAPI.dispatch(toastActions.toastError('画像と帯テンプレートを指定してください。'));
      return;
    }

    thunkAPI.dispatch(floorPlanExtractionActions.setIsLoading(true));

    // タイマー処理
    let runTime = 0;
    thunkAPI.dispatch(floorPlanExtractionActions.setRunTime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(floorPlanExtractionActions.setRunTime(runTime += 1));
    }, 1000);

    try {
      // TODO: 実際のAPI呼び出しを実装
      // モック: 2秒待機して成功を返す
      await new Promise(resolve => setTimeout(resolve, 2000));

      // モック結果
      const resultImage: FloorPlanAfterImage = {
        id: getRandId(),
        before_image_id: 0,
        serverURI: 'serverURI' in sourceImage ? sourceImage.serverURI : (sourceImage as FloorPlanImage).uri,
        fileName: `maisoku_band_${bandPosition}.jpg`,
        style: 'normal',
        aspectRatio: 'original',
      };

      thunkAPI.dispatch(floorPlanExtractionActions.setBandResultImage(resultImage));

      thunkAPI.dispatch(toastActions.toastSuccess('帯替えが完了しました。'));
    } catch (error) {
      console.error('Band replacement error:', error);
      thunkAPI.dispatch(toastActions.toastError('処理中にエラーが発生しました。'));
    } finally {
      clearInterval(timer);
      thunkAPI.dispatch(floorPlanExtractionActions.setIsLoading(false));
      thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate));
    }
  }
);

// Slice定義
const floorPlanExtractionSlice = createSlice({
  name: "floorPlanExtraction",
  initialState,
  reducers: {
    // 基本状態
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setStubId(state, action: PayloadAction<number>) {
      state.stubId = action.payload;
    },
    setRunTime(state, action: PayloadAction<number>) {
      state.runTime = action.payload;
    },

    // 画像管理
    setSourceImage(state, action: PayloadAction<FloorPlanImage | null>) {
      state.sourceImage = action.payload;
    },
    setSourceImageProcessed(state, action: PayloadAction<boolean>) {
      if (state.sourceImage) {
        state.sourceImage.isProcessed = action.payload;
      }
    },
    setSourceImageFailed(state, action: PayloadAction<boolean>) {
      if (state.sourceImage) {
        state.sourceImage.isProcessedFail = action.payload;
      }
    },
    addAfterImage(state, action: PayloadAction<FloorPlanAfterImage>) {
      state.afterImages.push(action.payload);
    },
    setSelectedAfterImage(state, action: PayloadAction<number | undefined>) {
      state.selectedAfterImage = action.payload;
    },

    // 選択モード
    setSelectionMode(state, action: PayloadAction<SelectionMode>) {
      state.selectionMode = action.payload;
      // モード切替時に選択範囲をクリア
      state.selectionArea = null;
      state.drawingLines = [];
    },
    setSelectionArea(state, action: PayloadAction<SelectionArea | null>) {
      state.selectionArea = action.payload;
    },
    setSelectionPreviewUrl(state, action: PayloadAction<string | null>) {
      state.selectionPreviewUrl = action.payload;
    },
    setSelectionPreviewBlob(state, action: PayloadAction<Blob | null>) {
      state.selectionPreviewBlob = action.payload;
    },

    // スタイル設定
    setSelectedStyle(state, action: PayloadAction<FloorPlanStyle>) {
      state.selectedStyle = action.payload;
    },

    // 描画設定
    setBrushSize(state, action: PayloadAction<number>) {
      state.brushSize = action.payload;
    },
    setDrawingLines(state, action: PayloadAction<number[][]>) {
      state.drawingLines = action.payload;
    },
    addDrawingLine(state, action: PayloadAction<number[]>) {
      state.drawingLines.push(action.payload);
    },
    clearDrawingLines(state) {
      state.drawingLines = [];
    },

    // UI状態
    setShowSelectionCanvas(state, action: PayloadAction<boolean>) {
      state.showSelectionCanvas = action.payload;
    },
    setShowConfirmPopup(state, action: PayloadAction<boolean>) {
      state.showConfirmPopup = action.payload;
    },
    setIsPhotoTooBigPopupShow(state, action: PayloadAction<boolean>) {
      state.isPhotoTooBigPopupShow = action.payload;
    },

    // 履歴管理
    pushToHistory(state) {
      if (state.selectionArea) {
        // 現在のインデックスより後の履歴を削除
        state.selectionHistoryStack = state.selectionHistoryStack.slice(0, state.selectionHistoryIndex + 1);
        state.selectionHistoryStack.push({ ...state.selectionArea });
        state.selectionHistoryIndex = state.selectionHistoryStack.length - 1;
      }
    },
    undoSelection(state) {
      if (state.selectionHistoryIndex > 0) {
        state.selectionHistoryIndex -= 1;
        state.selectionArea = state.selectionHistoryStack[state.selectionHistoryIndex];
      } else if (state.selectionHistoryIndex === 0) {
        state.selectionHistoryIndex = -1;
        state.selectionArea = null;
      }
    },
    redoSelection(state) {
      if (state.selectionHistoryIndex < state.selectionHistoryStack.length - 1) {
        state.selectionHistoryIndex += 1;
        state.selectionArea = state.selectionHistoryStack[state.selectionHistoryIndex];
      }
    },

    // データクリア
    clearData(state) {
      return {
        ...initialState,
        stubId: getRandId(),
      };
    },
    clearSelection(state) {
      state.selectionArea = null;
      state.selectionPreviewUrl = null;
      state.selectionPreviewBlob = null;
      state.drawingLines = [];
      state.selectionHistoryStack = [];
      state.selectionHistoryIndex = -1;
    },

    // 帯替え用アクション
    setBandSourceImage(state, action: PayloadAction<FloorPlanImage | null>) {
      state.bandSourceImage = action.payload;
    },
    setBandInputSource(state, action: PayloadAction<'upload' | 'extraction_result'>) {
      state.bandInputSource = action.payload;
    },
    setBandTemplates(state, action: PayloadAction<BandTemplate[]>) {
      state.bandTemplates = action.payload;
    },
    setBandTemplatesCount(state, action: PayloadAction<number>) {
      state.bandTemplatesCount = action.payload;
    },
    setBandTemplatesMaxCount(state, action: PayloadAction<number>) {
      state.bandTemplatesMaxCount = action.payload;
    },
    setBandTemplatesLoading(state, action: PayloadAction<boolean>) {
      state.bandTemplatesLoading = action.payload;
    },
    setLocalBandTemplate(state, action: PayloadAction<LocalBandTemplate | null>) {
      state.localBandTemplate = action.payload;
    },
    setSelectedBandTemplate(state, action: PayloadAction<BandTemplate | null>) {
      state.selectedBandTemplate = action.payload;
    },
    setBandPosition(state, action: PayloadAction<'top' | 'bottom'>) {
      state.bandPosition = action.payload;
      // 位置が変わったらオフセットをリセット
      state.bandYOffset = 0;
    },
    setBandYOffset(state, action: PayloadAction<number>) {
      state.bandYOffset = action.payload;
    },
    setBandScale(state, action: PayloadAction<number>) {
      state.bandScale = action.payload;
    },
    setBandResultImage(state, action: PayloadAction<FloorPlanAfterImage | null>) {
      state.bandResultImage = action.payload;
    },
    clearBandData(state) {
      state.bandSourceImage = null;
      state.bandInputSource = 'upload';
      state.selectedBandTemplate = null;
      state.localBandTemplate = null;
      state.bandYOffset = 0;
      state.bandScale = 1.0;
      state.bandResultImage = null;
      state.pdfConvertedPages = [];
      state.currentPdfPageIndex = 0;
    },

    // PDF複数ページ対応
    setPdfConvertedPages(state, action: PayloadAction<PdfConvertedPage[]>) {
      state.pdfConvertedPages = action.payload;
    },
    setCurrentPdfPageIndex(state, action: PayloadAction<number>) {
      state.currentPdfPageIndex = action.payload;
    },
    clearPdfPages(state) {
      state.pdfConvertedPages = [];
      state.currentPdfPageIndex = 0;
    },
  },
});

// Actionsをエクスポート
export const floorPlanExtractionActions = {
  ...floorPlanExtractionSlice.actions,
  addSourceImage,
  processFloorPlanExtraction,
  addBandSourceImage,
  addBandTemplate,
  fetchBandTemplates,
  saveBandTemplate,
  updateBandTemplate,
  deleteBandTemplate,
  processBandReplacement,
  consumeBandReplacementCredit,
};

// Reducerをエクスポート
export default floorPlanExtractionSlice.reducer;
